import { find, first } from "@prismicio/editor-support/Set"
import {
  ErrorBoundary,
  FilterField,
  type FilterOption,
} from "@prismicio/editor-ui"
import { Suspense, useRef, useState } from "react"

import {
  useRepositoryLocales,
  useRepositoryTags,
  useRepositoryTypes,
} from "./filterData"
import { transformFixedFilters } from "./useFilters"

interface DocumentsListFiltersProps {
  filteredLocales: Set<FilterOption>
  setFilteredLocales: (newValue: Set<FilterOption>) => void
  filteredTags: Set<FilterOption>
  setFilteredTags: (newValue: Set<FilterOption>) => void
  filteredCustomTypes: Set<FilterOption>
  setFilteredCustomTypes: (newValue: Set<FilterOption>) => void
  fixedCustomTypes?: Set<string>
  fixedTags?: Set<string>
}

export function DocumentsListFilters(props: DocumentsListFiltersProps) {
  const {
    filteredLocales,
    setFilteredLocales,
    filteredTags,
    setFilteredTags,
    filteredCustomTypes,
    setFilteredCustomTypes,
    fixedTags,
    fixedCustomTypes,
  } = props

  return (
    <>
      <LocalesFilter
        selectedValues={filteredLocales}
        onSelectedValuesChange={setFilteredLocales}
      />
      <Filter
        selectedValues={filteredTags}
        fixedSelectedValues={transformFixedFilters(fixedTags)}
        onSelectedValuesChange={setFilteredTags}
        label="Tags"
        icon="label"
        fetchFilters={useRepositoryTags}
      />
      <Filter
        selectedValues={filteredCustomTypes}
        fixedSelectedValues={transformFixedFilters(fixedCustomTypes)}
        onSelectedValuesChange={setFilteredCustomTypes}
        label="Type"
        icon="description"
        fetchFilters={useRepositoryTypes}
      />
    </>
  )
}

interface FilterProps {
  onSelectedValuesChange: (selectedValues: Set<FilterOption>) => void
  fixedSelectedValues?: Set<FilterOption>
  selectedValues: Set<FilterOption>
  label: string
  icon: "label" | "description" | "public"
  fetchFilters: () => Set<FilterOption>
  multiple?: boolean
}

export function Filter(props: FilterProps) {
  const [open, setOpen] = useState(false)

  const errorBoundary = useRef<ErrorBoundary>(null)

  return (
    <ErrorBoundary
      ref={errorBoundary}
      renderError={() => (
        <FilterField
          mode="error"
          onErrorRetry={errorBoundary.current?.reset}
          open={open}
          setOpen={setOpen}
          values={new Set()}
          {...props}
        />
      )}
    >
      <Suspense
        fallback={
          <FilterField
            mode="loading"
            open={open}
            setOpen={setOpen}
            values={new Set()}
            {...props}
          />
        }
      >
        <PopulatedFilter open={open} setOpen={setOpen} {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

interface PopulatedFilterProps extends FilterProps {
  open: boolean
  setOpen: (open: boolean) => void
}

function PopulatedFilter(props: PopulatedFilterProps) {
  const {
    open,
    setOpen,
    onSelectedValuesChange,
    fixedSelectedValues,
    selectedValues,
    label,
    icon,
    fetchFilters,
    multiple,
  } = props

  const filterOptions = fetchFilters()

  return (
    <FilterField
      open={open}
      setOpen={setOpen}
      label={label}
      icon={icon}
      selectedValues={selectedValues}
      values={new Set(filterOptions)}
      fixedSelectedValues={fixedSelectedValues}
      onSelectedValuesChange={onSelectedValuesChange}
      multiple={multiple}
    />
  )
}

interface LocalesFilterProps {
  selectedValues: Set<FilterOption>
  onSelectedValuesChange: (selectedValues: Set<FilterOption>) => void
}

export function LocalesFilter(props: LocalesFilterProps) {
  const { selectedValues, onSelectedValuesChange } = props

  const [displayedLocale, setDisplayedLocale] = useState(first(selectedValues))

  function onValueChange(selectedLocales: Set<FilterOption>) {
    onSelectedValuesChange(selectedLocales)
    setDisplayedLocale(first(selectedLocales))
  }

  const selectedLocaleObject = find(
    useRepositoryLocales(),
    (locale) => locale.id === first(selectedValues)?.id,
  )

  if (selectedLocaleObject?.label !== displayedLocale?.label)
    setDisplayedLocale(selectedLocaleObject)

  return (
    <Filter
      selectedValues={new Set(displayedLocale ? [displayedLocale] : [])}
      onSelectedValuesChange={onValueChange}
      label="Locale"
      icon="public"
      fetchFilters={useRepositoryLocales}
      multiple={false}
    />
  )
}
